import type { TransactionKind } from "@stepfinance/transaction-history";
import type { FC } from "react";
import React from "react";

import { ReactComponent as BalanceIcon } from "../../assets/balances.svg";
import { ReactComponent as AddLiquidityIcon } from "../../assets/transaction-history/add-liquidity.svg";
import { ReactComponent as ClaimIcon } from "../../assets/transaction-history/claim.svg";
import { ReactComponent as DepositIcon } from "../../assets/transaction-history/deposit.svg";
import { ReactComponent as DonationIcon } from "../../assets/transaction-history/donation.svg";
import { ReactComponent as ReceiveIcon } from "../../assets/transaction-history/receive.svg";
import { ReactComponent as RemoveLiquidityIcon } from "../../assets/transaction-history/remove-liquidity.svg";
import { ReactComponent as SendIcon } from "../../assets/transaction-history/send.svg";
import { ReactComponent as StakeIcon } from "../../assets/transaction-history/stake.svg";
import { ReactComponent as SwapIcon } from "../../assets/transaction-history/swap.svg";
import { ReactComponent as UnstakeIcon } from "../../assets/transaction-history/unstake.svg";
import { ReactComponent as WithdrawIcon } from "../../assets/transaction-history/withdraw.svg";
import { TokenIconText } from "../shared";

export type TransactionHistoryIconTypes =
  | Exclude<TransactionKind, "Transfer" | "Margin">
  | "Send"
  | "Receive"
  | "Deposit"
  | "Withdraw"
  | "Stake + Claim"
  | "Unstake + Claim";

export interface ActionTypes {
  value: TransactionHistoryIconTypes;
  text?: string;
}

export const TX_HISTORY_ACTIONS_TYPES: Record<TransactionHistoryIconTypes, ActionTypes> = {
  Send: {
    value: "Send",
  },
  Unknown: {
    value: "Unknown",
  },
  Receive: {
    value: "Receive",
  },
  "Stake + Claim": {
    value: "Stake + Claim",
  },
  "Unstake + Claim": {
    value: "Unstake + Claim",
  },
  Stake: {
    value: "Stake",
  },
  Unstake: {
    value: "Unstake",
  },
  Claim: {
    value: "Claim",
  },
  Swap: {
    value: "Swap",
  },
  "Jupiter Swap": {
    value: "Jupiter Swap",
    text: "Swap",
  },
  Donation: {
    value: "Donation",
  },
  Deposit: {
    value: "Deposit",
  },
  Withdraw: {
    value: "Withdraw",
  },
  "Add Liquidity": {
    value: "Add Liquidity",
  },
  "Remove Liquidity": {
    value: "Remove Liquidity",
  },
  "Step In": {
    value: "Step In",
  },
  "Step Out": {
    value: "Step Out",
  },
  "Withdraw One": {
    value: "Withdraw One",
    text: "Remove Liquidity",
  },
  "Deposit Collateral": {
    value: "Deposit Collateral",
  },
  "Withdraw Collateral": {
    value: "Withdraw Collateral",
  },
  Borrow: {
    value: "Borrow",
  },
  Repay: {
    value: "Repay",
  },
};

export const TRANSACTION_HISTORY_ICON_COMPONENT_MAP: Record<string, FC> = {
  [TX_HISTORY_ACTIONS_TYPES.Send.value]: () => (
    <>
      <SendIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Send.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Receive.value]: () => (
    <>
      <ReceiveIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Receive.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Stake + Claim"].value]: () => (
    <>
      <StakeIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Stake + Claim"].value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Unstake + Claim"].value]: () => (
    <>
      <UnstakeIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Unstake + Claim"].value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Stake.value]: () => (
    <>
      <StakeIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Stake.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Unstake.value]: () => (
    <>
      <UnstakeIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Unstake.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Claim.value]: () => (
    <>
      <ClaimIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Claim.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Swap.value]: () => (
    <>
      <SwapIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Swap.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Jupiter Swap"].value]: () => (
    <>
      <SwapIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Jupiter Swap"].text}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Donation.value]: () => (
    <>
      <DonationIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Donation.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Deposit.value]: () => (
    <>
      <DepositIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Deposit.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Withdraw.value]: () => (
    <>
      <WithdrawIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Withdraw.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Add Liquidity"].value]: () => (
    <>
      <AddLiquidityIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Add Liquidity"].value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Remove Liquidity"].value]: () => (
    <>
      <RemoveLiquidityIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Remove Liquidity"].value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Step In"].value]: () => (
    <>
      <StakeIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Step In"].value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Step Out"].value]: () => (
    <>
      <UnstakeIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Step Out"].value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Withdraw One"].value]: () => (
    <>
      <RemoveLiquidityIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Withdraw One"].text}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Deposit Collateral"].value]: () => (
    <>
      <DepositIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Deposit Collateral"].value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Borrow.value]: () => (
    <>
      <BalanceIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Borrow.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES.Repay.value]: () => (
    <>
      <BalanceIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES.Repay.value}</TokenIconText>
    </>
  ),
  [TX_HISTORY_ACTIONS_TYPES["Withdraw Collateral"].value]: () => (
    <>
      <WithdrawIcon />
      <TokenIconText>{TX_HISTORY_ACTIONS_TYPES["Withdraw Collateral"].value}</TokenIconText>
    </>
  ),
};

export const getTransactionHistoryIconComponent = (type: TransactionHistoryIconTypes): FC =>
  TRANSACTION_HISTORY_ICON_COMPONENT_MAP[type];
